'use client'

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

interface ResizableContextValue {
  isResizing: boolean
}

const ResizableContext = createContext<ResizableContextValue>({
  isResizing: false,
})

export const useResizable = () => useContext(ResizableContext)

export const mediaResizeHandleVariants = cva(
  cn(
    'absolute top-1/2 -translate-y-1/2 flex w-6 flex-col justify-center select-none h-full cursor-col-resize',
    'transition-all duration-200 ease-out',
    "after:flex after:h-12 after:w-[2px]  after:rounded-full after:bg-transparent after:opacity-30 after:content-[''] after:transition-all after:duration-200",
    'hover:after:opacity-100 hover:after:bg-blue-500 hover:after:w-[3px] hover:after:h-16',
    'group-hover/media:after:opacity-60'
  ),
  {
    variants: {
      direction: {
        left: 'left-1 -ml-3 pl-3 items-start',
        right: 'right-1 -mr-3 pr-3 items-end',
      },
    },
  }
)

interface ResizeHandleProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onMouseDown'> {
  direction: 'left' | 'right'
  onResize: (delta: number) => void
  onResizeEnd?: () => void
  onMouseDown?: () => void
}

const ResizeHandle: React.FC<ResizeHandleProps> = ({
  direction,
  onResize,
  onResizeEnd,
  onMouseDown: onMouseDownProp,
  className,
  ...props
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const startX = useRef(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      console.log('🔧 [ResizeHandle] Mouse down on', direction, 'handle')
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
      startX.current = e.clientX

      // Call parent's onMouseDown
      onMouseDownProp?.()

      // Add visual feedback
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [onMouseDownProp, direction]
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return

      e.preventDefault()
      const deltaX = e.clientX - startX.current
      const delta = direction === 'left' ? -deltaX : deltaX

      console.log('🔧 [ResizeHandle] Moving:', direction, 'delta:', delta)
      onResize(delta)
      startX.current = e.clientX
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (isDragging) {
        console.log('🔧 [ResizeHandle] Mouse up on', direction, 'handle')
        e.preventDefault()
        setIsDragging(false)
        onResizeEnd?.()

        // Reset cursor
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, {
        passive: false,
      })
      document.addEventListener('mouseup', handleMouseUp, { passive: false })
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, onResize, onResizeEnd, direction])

  return (
    <div
      className={cn(mediaResizeHandleVariants({ direction }), className)}
      onMouseDown={handleMouseDown}
      style={{ zIndex: 9999 }} // Ensure it's above interaction points overlay
      {...props}
    />
  )
}

interface ResizableProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right'
  onWidthChange: (width: number) => void
  width: number
  minWidth?: number
  maxWidth?: number
  readOnly?: boolean
}

export const Resizable: React.FC<ResizableProps> = ({
  children,
  align = 'center',
  onWidthChange,
  width,
  minWidth = 100,
  maxWidth = 1200,
  readOnly = false,
  className,
  ...props
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const [currentWidth, setCurrentWidth] = useState(width)
  const containerRef = useRef<HTMLDivElement>(null)
  const pendingWidth = useRef(width)

  useEffect(() => {
    setCurrentWidth(width)
    pendingWidth.current = width
  }, [width])

  const handleResize = useCallback(
    (delta: number) => {
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, pendingWidth.current + delta)
      )
      console.log(
        '🔧 [Resizable] Resize:',
        pendingWidth.current,
        '+',
        delta,
        '=',
        newWidth
      )
      pendingWidth.current = newWidth
      setCurrentWidth(newWidth)
    },
    [minWidth, maxWidth]
  )

  const handleResizeStart = useCallback(() => {
    // console.log('🔧 [Resizable] Resize start, current width:', currentWidth)
    setIsResizing(true)
    pendingWidth.current = currentWidth
  }, [currentWidth])

  const handleResizeEnd = useCallback(() => {
    // console.log('🔧 [Resizable] Resize end, final width:', pendingWidth.current)
    setIsResizing(false)
    onWidthChange(pendingWidth.current)
  }, [onWidthChange])

  const alignmentClass = {
    left: 'mr-auto',
    center: 'mx-auto',
    right: 'ml-auto',
  }[align]

  return (
    <ResizableContext.Provider value={{ isResizing }}>
      <div
        ref={containerRef}
        className={cn('relative max-w-fit', alignmentClass, className)}
        style={{ width: currentWidth }}
        {...props}
      >
        <div className='group/media relative w-full'>
          {children}
          {!readOnly && (
            <>
              <ResizeHandle
                direction='left'
                onResize={handleResize}
                onResizeEnd={handleResizeEnd}
                onMouseDown={handleResizeStart}
              />
              <ResizeHandle
                direction='right'
                onResize={handleResize}
                onResizeEnd={handleResizeEnd}
                onMouseDown={handleResizeStart}
              />
            </>
          )}
        </div>
      </div>
    </ResizableContext.Provider>
  )
}
